// src/Services/pdf/PdfTableExporter.js
/**
 * PdfTableExporter — Resolve columns/rows for PDF export from various sources.
 * Priority: explicit props > vue-good-table via ref > native HTML tables in scope.
 * Junior-friendly, UI-only; no API calls.
 */

/**
 * Strip vue-good-table's accessible sort helper and normalize whitespace.
 * Example helper text: "Sort table by Item in descending order".
 * @param {string} raw
 * @returns {string}
 */
function cleanVgtHeaderText(raw) {
  if (!raw) return '';
  let text = String(raw).replace(/\s+/g, ' ').trim();
  const idx = text.toLowerCase().indexOf('sort table by');
  if (idx !== -1) text = text.slice(0, idx).trim();
  return text;
}

/** Convert a native HTML table element to { columns, rows } */
export function htmlTableToData(table) {
  const getText = (el) => (el && (el.innerText || el.textContent) || '').trim();
  const ths = Array.from(table.querySelectorAll('thead th'));
  const header = ths.length
    ? ths.map(th => cleanVgtHeaderText(getText(th)))
    : Array.from(table.querySelectorAll('tr:first-child td')).map(td => cleanVgtHeaderText(getText(td)));
  const bodyRows = Array.from(table.querySelectorAll('tbody tr')).map(tr =>
    Array.from(tr.children).map(td => getText(td))
  );
  const columns = header.map(h => ({ key: h, label: h }));
  return { columns, rows: bodyRows };
}

function normalizeColumns(cols) {
  return (cols || []).map(c => {
    if (typeof c === 'string') return { key: c, label: c };
    const key = c.key || c.field || c.label; // support vue-good-table's `field`
    const label = c.label || c.key || c.field || '';
    return { key, label, align: c.align };
  });
}

function findExportableContainer(el) {
  let node = el; while (node && node !== document) {
    if (node.hasAttribute && (node.hasAttribute('data-exportable') || node.hasAttribute('data-export-scope'))) return node;
    node = node.parentNode;
  }
  return null;
}

function mergeTables(tables) {
  // To avoid duplicate content (e.g., separate header/body tables), export ONLY the first table by default.
  // If a future need arises to merge, this can be extended with an option.
  const first = tables[0];
  return htmlTableToData(first);
}

function pickFieldValue(row, field) {
  if (!field || row == null) return row && row[field];
  if (typeof field === 'function') {
    try {
      return field(row);
    } catch (_) {
      return undefined;
    }
  }
  if (typeof field === 'string' && field.includes('.')) {
    return field.split('.').reduce((acc, key) => (acc != null ? acc[key] : undefined), row);
  }
  return row[field];
}

function flattenVgtRows(list = []) {
  const acc = [];
  list.forEach(header => {
    const kids = (header && header.children) || [];
    kids.forEach(r => acc.push(r.original || r));
  });
  return acc;
}

function findVueGoodTableInVm(vm, el) {
  if (!vm || !vm.$el || (el && !vm.$el.contains(el))) return null;
  const name = ((vm.$options && (vm.$options.name || vm.$options._componentTag)) || '').toString().toLowerCase();
  if (name.includes('vue-good-table')) return vm;
  for (const child of vm.$children || []) {
    if (child.$el && child.$el.contains(el)) {
      const match = findVueGoodTableInVm(child, el);
      if (match) return match;
    }
  }
  return null;
}

function findVueGoodTableInstance(el) {
  if (!el) return null;
  const candidates = [];
  const root = el.closest ? el.closest('.vgt-wrap') : null;
  if (root && root.__vue__) candidates.push(root.__vue__);
  let node = el;
  while (node) {
    if (node.__vue__ && !candidates.includes(node.__vue__)) {
      candidates.push(node.__vue__);
    }
    node = node.parentElement;
  }
  for (const vm of candidates) {
    const found = findVueGoodTableInVm(vm, el);
    if (found) return found;
  }
  return null;
}

function resolveFromVueGoodTable(vgt, fallbackTableEl) {
  if (!vgt) return { columns: [], rows: [] };
  const propColumns = (vgt.$props && (vgt.$props.columns || vgt.$props.cols)) || [];
  const domLabels = (() => {
    try {
      const t = (vgt.$el && vgt.$el.querySelector && vgt.$el.querySelector('table')) || fallbackTableEl;
      if (!t) return [];
      return Array.from(t.querySelectorAll('thead th')).map(th => cleanVgtHeaderText((th.innerText || th.textContent || '').trim()));
    } catch (_) {
      return [];
    }
  })();
  const columns = normalizeColumns(propColumns).map((c, idx) => ({
    key: c.key,
    label: c.label || domLabels[idx] || c.key,
    align: c.align
  }));

  let rawRows = [];
  if (Array.isArray(vgt.processedRows) && vgt.processedRows.length) {
    rawRows = flattenVgtRows(vgt.processedRows);
  } else if (Array.isArray(vgt.filteredRows) && vgt.filteredRows.length) {
    rawRows = flattenVgtRows(vgt.filteredRows);
  } else if (Array.isArray(vgt.originalRows) && vgt.originalRows.length) {
    rawRows = flattenVgtRows(vgt.originalRows);
  }
  if (!rawRows.length && vgt.$props && Array.isArray(vgt.$props.rows)) {
    rawRows = vgt.$props.rows.slice();
  }

  const rows = rawRows.map(r => columns.map(col => pickFieldValue(r, col.key)));
  return { columns, rows };
}

function resolveFromElement(el) {
  if (!el) return { columns: [], rows: [] };
  const vgt = findVueGoodTableInstance(el);
  if (vgt) return resolveFromVueGoodTable(vgt, el.querySelector ? el.querySelector('table') : null);
  if (el.tagName && el.tagName.toLowerCase() === 'table') {
    return htmlTableToData(el);
  }
  if (el.querySelector) {
    const table = el.querySelector('table');
    if (table) return htmlTableToData(table);
  }
  return { columns: [], rows: [] };
}

export const PdfTableExporter = {
  /**
   * Resolve columns/rows for export.
   * @param {Vue} ctx - component instance (for $refs)
   * @param {Object} opts
   * @param {string} [opts.tableRef]
   * @param {string} [opts.selector]
   * @param {string[]} [opts.selectors]
   * @param {Array} [opts.rows]
   * @param {Array} [opts.columns]
   * @returns {{columns:Array, rows:Array}}
   */
  resolve(ctx, opts = {}) {
    // 1) Explicit
    if (opts.rows && opts.columns) return { columns: normalizeColumns(opts.columns), rows: opts.rows };

    // 2) vue-good-table via ref
    if (opts.tableRef && ctx && ctx.$refs && ctx.$refs[opts.tableRef]) {
      // tableRef may point to wrapper; drill into child vue-good-table if needed
      const refComp = ctx.$refs[opts.tableRef];
      const vgt = findVueGoodTableInVm(refComp, refComp && refComp.$el ? refComp.$el : null) || refComp;
      return resolveFromVueGoodTable(vgt);
    }

    // 3) Native tables by selector(s) or nearest in scope
    const tables = [];
    if (Array.isArray(opts.selectors) && opts.selectors.length) {
      opts.selectors.forEach(sel => document.querySelectorAll(sel).forEach(t => tables.push(t)));
    } else if (opts.selector) {
      document.querySelectorAll(opts.selector).forEach(t => tables.push(t));
    } else if (ctx && ctx.$el) {
      const host = findExportableContainer(ctx.$el);
      if (host) host.querySelectorAll('table').forEach(t => tables.push(t));
    }

    if (tables.length) {
      // Prefer vue-good-table extraction if present; iterate until we find data
      for (const t of tables) {
        const resolved = resolveFromElement(t);
        if (resolved && (resolved.columns.length || resolved.rows.length)) return resolved;
      }
      return mergeTables(tables);
    }

    // Last resort: try resolving from ctx root element (first table or vgt in scope)
    if (ctx && ctx.$el) {
      const resolved = resolveFromElement(ctx.$el);
      if (resolved.columns.length || resolved.rows.length) return resolved;
    }

    return { columns: [], rows: [] };
  }
};
